import { STAFF_ROLE_DEFAULTS, STAFF_ROLES } from '@structural-climbing/auth'
import type { StaffPermissions, StaffRole } from '@structural-climbing/auth'

export interface GymFeatures {
  productsEnabled: boolean
  competitionsEnabled: boolean
}

/** Whether the Permissions dialog offers this toggle in this gym. A hidden one
 *  cannot be ticked, so it must not count against anyone. */
export function isPermissionOffered(key: keyof StaffPermissions, gym: GymFeatures): boolean {
  if (key === 'competitions') return gym.competitionsEnabled
  if (key === 'products') return gym.productsEnabled
  return true
}

/** Every permission key there is, taken from a role's defaults (each lists them all). */
const ALL_KEYS = Object.keys(STAFF_ROLE_DEFAULTS.front_desk) as (keyof StaffPermissions)[]

function covers(permissions: StaffPermissions, role: StaffRole, gym: GymFeatures): boolean {
  const defaults = STAFF_ROLE_DEFAULTS[role]
  return ALL_KEYS.filter((k) => defaults[k] === true && isPermissionOffered(k, gym)).every(
    (k) => permissions[k] === true,
  )
}

/**
 * The job titles a staff member's permissions add up to. Someone invited as a
 * trainer and then given every box is also front desk and a route setter, and
 * the Team page should say so rather than keep calling them a trainer.
 *
 * The invited role always comes first, so the column never loses it. Another
 * title shows when the person holds every permission that title gets by default.
 */
export function staffTitles(
  staffRole: StaffRole | null,
  permissions: StaffPermissions,
  gym: GymFeatures,
): { roles: StaffRole[]; fullAccess: boolean } {
  const roles: StaffRole[] = staffRole ? [staffRole] : []
  for (const role of STAFF_ROLES) {
    if (role !== staffRole && covers(permissions, role, gym)) roles.push(role)
  }
  const fullAccess = ALL_KEYS.filter((k) => isPermissionOffered(k, gym)).every(
    (k) => permissions[k] === true,
  )
  return { roles, fullAccess }
}
